import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';
import { getVideoAddress, type VideoData } from '@/lib/videoTypes';

export interface VideoComment {
  event: NostrEvent;
  /** 'nip22' for kind 1111 comments, 'legacy' for kind 1 notes. */
  standard: 'nip22' | 'legacy';
  /** Event ID of the parent comment, when this is a reply. */
  parentId?: string;
  replies: VideoComment[];
}

/**
 * Fetch both NIP-22 (kind 1111) and legacy kind 1 comments for a video and
 * arrange them into a thread tree. Kind 1 comments are marked as non-standard
 * in the UI.
 */
export function useVideoComments(video: VideoData | null) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['comments', video?.id, video?.dTag],
    queryFn: async ({ signal }) => {
      if (!video) return [] as VideoComment[];

      const address = getVideoAddress(video);
      const filters: NostrFilter[] = [];

      // NIP-22 comments on the root scope
      const nip22Filter: NostrFilter = { kinds: [1111], limit: 200 };
      if (address) {
        nip22Filter['#A'] = [address];
      } else {
        nip22Filter['#E'] = [video.id];
      }
      filters.push(nip22Filter);

      // Legacy kind 1 comments (non-standard for videos, but widely used)
      filters.push({ kinds: [1], '#e': [video.id], limit: 200 });
      if (address) {
        filters.push({ kinds: [1], '#a': [address], limit: 200 });
      }

      const events = await nostr.query(filters, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]),
      });

      const unique = Array.from(new Map(events.map(e => [e.id, e])).values());

      const flat: VideoComment[] = unique
        .map((event): VideoComment | null => {
          if (event.kind === 1111) {
            // Parent = lowercase e tag. For top-level comments the parent
            // equals the root scope (the video itself), so it's not a reply.
            const eTags = event.tags.filter(t => t[0] === 'e' && t[1]);
            const parentId = eTags.length > 0 ? eTags[eTags.length - 1][1] : undefined;
            const isReply = parentId !== undefined && parentId !== video.id;
            return {
              event,
              standard: 'nip22',
              parentId: isReply ? parentId : undefined,
              replies: [],
            };
          }
          if (event.kind === 1) {
            // Legacy: e tag with "reply" marker = reply; "root" = top-level.
            // Fallback for unmarked tags (positional convention): when there
            // are multiple e tags, the last one that isn't the video itself
            // is the parent.
            const eTags = event.tags.filter(t => t[0] === 'e' && t[1]);
            const replyTag = eTags.find(t => t[3] === 'reply');
            let parentId = replyTag?.[1];
            if (!parentId) {
              const others = eTags.filter(t => t[1] !== video.id);
              if (others.length > 0) parentId = others[others.length - 1][1];
            }
            const isReply = parentId !== undefined && parentId !== video.id;
            return {
              event,
              standard: 'legacy',
              parentId: isReply ? parentId : undefined,
              replies: [],
            };
          }
          return null;
        })
        .filter((c): c is VideoComment => c !== null);

      // Build thread tree (one level of replies; orphans become top-level)
      const byId = new Map(flat.map(c => [c.event.id, c]));
      const roots: VideoComment[] = [];
      for (const comment of flat) {
        const parent = comment.parentId ? byId.get(comment.parentId) : undefined;
        if (parent) {
          parent.replies.push(comment);
        } else {
          roots.push(comment);
        }
      }

      // Sort: newest first at the top, oldest first within replies
      roots.sort((a, b) => b.event.created_at - a.event.created_at);
      for (const root of roots) {
        root.replies.sort((a, b) => a.event.created_at - b.event.created_at);
      }

      return roots;
    },
    enabled: !!video,
  });
}

export interface PostCommentInput {
  content: string;
  /** Comment kind: 1111 (NIP-22, standard) or 1 (legacy). */
  kind: 1111 | 1;
  /** When replying to another comment. */
  parent?: VideoComment;
}

/** Post a comment on a video as NIP-22 kind 1111 or legacy kind 1. */
export function usePostComment(video: VideoData | null) {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: PostCommentInput) => {
      if (!user) throw new Error('You must be logged in to comment');
      if (!video) throw new Error('No video loaded');
      if (!input.content.trim()) throw new Error('Comment cannot be empty');

      const address = getVideoAddress(video);
      const parentEvent = input.parent?.event;
      const tags: string[][] = [];

      if (input.kind === 1111) {
        // NIP-22: uppercase tags = root scope, lowercase = parent item
        if (address) {
          tags.push(['A', address]);
        } else {
          tags.push(['E', video.id, '', video.pubkey]);
        }
        tags.push(['K', String(video.originalEvent.kind)]);
        tags.push(['P', video.pubkey]);

        if (parentEvent) {
          // Replying to a comment (kind 1111 or kind 1 — both are valid
          // parents per current NIP-22).
          tags.push(['e', parentEvent.id, '', parentEvent.pubkey]);
          tags.push(['k', String(parentEvent.kind)]);
          tags.push(['p', parentEvent.pubkey]);
        } else {
          // Top-level: parent = root scope
          if (address) {
            tags.push(['a', address]);
          }
          // Always include an e tag referencing the video event itself
          tags.push(['e', video.id, '', video.pubkey]);
          tags.push(['k', String(video.originalEvent.kind)]);
          tags.push(['p', video.pubkey]);
        }
      } else {
        // Legacy kind 1 with positional markers
        if (parentEvent) {
          if (address) {
            tags.push(['a', address, '', 'root']);
          } else {
            tags.push(['e', video.id, '', 'root']);
          }
          tags.push(['e', parentEvent.id, '', 'reply']);
          tags.push(['p', video.pubkey]);
          tags.push(['p', parentEvent.pubkey]);
        } else {
          tags.push(['e', video.id, '', 'root']);
          if (address) {
            tags.push(['a', address]);
          }
          tags.push(['p', video.pubkey]);
        }
      }

      await createEvent({
        kind: input.kind,
        content: input.content.trim(),
        tags,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', video?.id, video?.dTag] });
      toast({ title: 'Comment posted' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to post comment',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}
